(() => {
  const WRAPPER_SELECTOR = '.custom_upsells_wrapper';
  const productMap = new Map();
  let observerStarted = false;
  let globalEventsBound = false;

  /* -------------------------------------------
     BOOT / INIT
  ------------------------------------------- */
  function boot() {
    startObserver();
    bindGlobalEvents();
    initExistingWrappers();
  }

  function startObserver() {
    if (observerStarted) return;
    observerStarted = true;

    const obs = new MutationObserver(() => {
      initExistingWrappers();
    });

    obs.observe(document.documentElement, { childList: true, subtree: true });
  }

  function bindGlobalEvents() {
    if (globalEventsBound) return;
    globalEventsBound = true;

    document.addEventListener('cart:refresh', debounceInitExistingWrappers, true);
    document.addEventListener('cart:updated', debounceInitExistingWrappers, true);
    document.addEventListener('shopify:section:load', debounceInitExistingWrappers, true);
    document.addEventListener('shopify:section:reorder', debounceInitExistingWrappers, true);
    document.addEventListener('shopify:section:select', debounceInitExistingWrappers, true);
    document.addEventListener('shopify:section:deselect', debounceInitExistingWrappers, true);
    window.addEventListener('pageshow', debounceInitExistingWrappers);
  }

  function debounceInitExistingWrappers() {
    clearTimeout(window.__customUpsellsInitTimer);
    window.__customUpsellsInitTimer = setTimeout(() => {
      initExistingWrappers();
    }, 80);
  }

  function initExistingWrappers() {
    document.querySelectorAll(WRAPPER_SELECTOR).forEach(wrapper => {
      if (!wrapper.dataset.upsellsInit) initUpsells(wrapper);
    });
  }

  function initUpsells(wrapper) {
    wrapper.dataset.upsellsInit = "1";
    const url = wrapper.getAttribute('data-reco-url');
    const archiveIds = new Set(safeParse(wrapper.dataset.archiveProductIds, []).map(String));
    if (!url) return;

    wrapper.innerHTML = `<div class="custom_upsells_loading">Loading...</div>`;

    fetch(url, { credentials: 'same-origin' })
      .then(r => r.json())
      .then(data => {
        const products = data?.products || [];
        if (!products.length) { wrapper.innerHTML = ''; return; }

        wrapper.innerHTML = `
          <div class="custom_upsells_section">
            <h4 class="cart-upsells__heading t-mt-0 t-mb-4 h4 t-text-center">You may also like</h4>

            <div class="swiper custom-upsells-swiper">
              <div class="swiper-wrapper"></div>
            </div>
          </div>
        `;

        const swiperEl = wrapper.querySelector('.custom-upsells-swiper');
        const track = swiperEl.querySelector('.swiper-wrapper');

        products.forEach(p => {
          if ((p.product_type || '').toLowerCase() === 'bundle') return;
          if (!p.handle || !p.variants?.length) return;

          productMap.set(p.handle, p);

          const baseImg = p.featured_image || p.images?.[0] || '';
          const title = escapeHtml(upsellDisplayTitle(p, archiveIds));
          const productUrl = `${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(p.handle)}`;

          track.insertAdjacentHTML('beforeend', `
            <div class="swiper-slide">
              <div class="custom_upsell_card" data-handle="${p.handle}" data-selected="{}">
                <div class="custom_upsell_top">
                <span class="custom_upsell_img">
                    ${baseImg ? `<img class="custom_upsell_img_tag" src="${baseImg}" alt="${title}" loading="lazy">` : ``}
                </span>

                  <div>
                    <div class="custom_upsell_headline">
                      <a class="custom_upsell_title_link t-font-medium" href="${productUrl}">${title}</a>
                      <div class="custom_upsell_price"></div>
                    </div>

                    <div class="options_wrapper">
                    <div class="custom_upsell_selected">Select options</div>

                    <div class="custom_upsell_headline">
                      <span></span>
                      <button type="button" class="custom_upsell_edit" aria-expanded="false">Edit</button>
                    </div>
                    </div>
                  </div>
                </div>

                <div class="custom_upsell_options"></div>

                <p class="custom_upsell_error" role="alert" hidden></p>
                <button class="custom_upsell_add" type="button" disabled data-variant-id="">
                  ADD
                </button>
              </div>
            </div>
          `);
        });

        if (!track.children.length) { wrapper.innerHTML = ''; return; }

        wrapper.querySelectorAll('.custom_upsell_card').forEach(card => {
          buildOptionPickers(card);
          autoSelectFirstAvailable(card);
        });

        initSwiper(swiperEl);
        attachInteractions(wrapper);
      })
      .catch(err => {
        console.error('Upsells error:', err);
        wrapper.innerHTML = '';
      });
  }

  /* -------------------------------------------
     SWIPER: 1.5 slides, no center
  ------------------------------------------- */
  function initSwiper(swiperEl) {
    if (!swiperEl || !window.Swiper) return;

    if (swiperEl.swiper) {
      try { swiperEl.swiper.destroy(true, true); } catch(e){}
    }

    new Swiper(swiperEl, {
      slidesPerView: 1.5,
      slidesPerGroup: 1,
      centeredSlides: false,
      spaceBetween: 12,
      freeMode: false,
      speed: 350,
      preventClicks: false,
      preventClicksPropagation: false,
      touchStartPreventDefault: false,
      observer: true,
      observeParents: true,
      watchOverflow: true
    });
  }

  /* -------------------------------------------
     BUILD OPTION PICKERS
  ------------------------------------------- */
  function buildOptionPickers(card) {
    const handle = card.dataset.handle;
    const p = productMap.get(handle);
    if (!p) return;

    const optsEl = card.querySelector('.custom_upsell_options');
    optsEl.innerHTML = '';

    const variants = p.variants || [];
    const optionNames = normalizeOptionNames(p);

    if (variants.length === 1) {
      const edit = card.querySelector('.custom_upsell_edit');
      if (edit) edit.style.display = 'none';
      return;
    }

    [1, 2, 3].forEach(idx => {
      const key = `option${idx}`;
      const values = [...new Set(variants.map(v => v[key]).filter(Boolean))];
      if (!values.length) return;

      const name = optionNames[idx - 1] || `Option ${idx}`;
      const isColor = /color|colour/i.test(name);

      optsEl.insertAdjacentHTML('beforeend', `
        <div class="upsell_option" data-option-index="${idx}">
          <div class="upsell_option_label">${escapeHtml(name)}</div>
          <div class="upsell_option_values">
            ${values.map(rawVal => {
              const label = escapeHtml(rawVal);
              const encoded = encodeURIComponent(rawVal);

              if (isColor) {
                const Value_ = rawVal.toLowerCase().replace(/\s+/g, '');
                const swatchImg_ = `https://myntlab.com/cdn/shop/files/${Value_}fabricswatch.jpg?width=60`;

                return `
                  <button type="button"
                    class="upsell_opt_btn is-swatch-img"
                    data-value="${encoded}"
                    title="${label}"
                    style="background-image:url('${swatchImg_}')">
                    <span class="visually-hidden">${label}</span>
                  </button>
                `;
              }

              return `
                <button type="button"
                  class="upsell_opt_btn"
                  data-value="${encoded}">${label}</button>
              `;
            }).join('')}
          </div>
        </div>
      `);
    });

    if (!optsEl.children.length) {
      const edit = card.querySelector('.custom_upsell_edit');
      if (edit) edit.style.display = 'none';
    }
  }

  /* -------------------------------------------
     AUTO SELECT FIRST AVAILABLE VARIANT
  ------------------------------------------- */
  function autoSelectFirstAvailable(card) {
    const handle = card.dataset.handle;
    const p = productMap.get(handle);
    if (!p) return;

    const variants = p.variants || [];
    const addBtn = card.querySelector('.custom_upsell_add');
    const priceEl = card.querySelector('.custom_upsell_price');
    const selectedEl = card.querySelector('.custom_upsell_selected');

    const firstAvail = variants.find(v => v.available) || variants[0];
    if (!firstAvail) return;

    if (variants.length === 1) {
      addBtn.disabled = !firstAvail.available;
      addBtn.dataset.variantId = firstAvail.id;
      if (priceEl) priceEl.textContent = formatMoney(firstAvail.price);
      setCardImage(card, p, firstAvail);
      if (selectedEl) selectedEl.textContent = firstAvail.available ? 'Ready to add' : 'Sold out';
      return;
    }

    const selected = {};
    if (firstAvail.option1) selected.option1 = firstAvail.option1;
    if (firstAvail.option2) selected.option2 = firstAvail.option2;
    if (firstAvail.option3) selected.option3 = firstAvail.option3;

    card.dataset.selected = JSON.stringify(selected);

    Object.keys(selected).forEach(k => {
      const idx = Number(k.replace('option', ''));
      const val = selected[k];

      const wrap = card.querySelector(`.upsell_option[data-option-index="${idx}"]`);
      if (!wrap) return;

      wrap.querySelectorAll('.upsell_opt_btn').forEach(b => b.classList.remove('is-active'));

      const btn = [...wrap.querySelectorAll('.upsell_opt_btn')]
        .find(b => decodeURIComponent(b.dataset.value || '') === val);

      if (btn) btn.classList.add('is-active');
    });

    updateVariantForCard(card);
  }

  /* -------------------------------------------
     INTERACTIONS
  ------------------------------------------- */
  function attachInteractions(wrapper) {
    if (wrapper.dataset.upsellsBound === "1") return;
    wrapper.dataset.upsellsBound = "1";

    wrapper.addEventListener('click', async (e) => {
      const edit = e.target.closest('.custom_upsell_edit');
      if (edit) {
        const card = edit.closest('.custom_upsell_card');
        if (!card) return;

        card.classList.toggle('is-editing');
        edit.textContent = card.classList.contains('is-editing') ? 'Close' : 'Edit';
        edit.setAttribute('aria-expanded', String(card.classList.contains('is-editing')));
        return;
      }

      const optBtn = e.target.closest('.upsell_opt_btn');
      if (optBtn) {
        const card = optBtn.closest('.custom_upsell_card');
        const optWrap = optBtn.closest('.upsell_option');
        if (!card || !optWrap) return;

        optWrap.querySelectorAll('.upsell_opt_btn').forEach(b => b.classList.remove('is-active'));
        optBtn.classList.add('is-active');

        const idx = Number(optWrap.dataset.optionIndex);
        const rawVal = decodeURIComponent(optBtn.dataset.value || '');

        const selected = safeParse(card.dataset.selected, {});
        selected[`option${idx}`] = rawVal;
        card.dataset.selected = JSON.stringify(selected);

        updateVariantForCard(card);
        return;
      }

      const addBtn = e.target.closest('.custom_upsell_add');
      if (addBtn) {
        const variantId = addBtn.dataset.variantId;
        if (!variantId) return;

        addBtn.disabled = true;
        const errorEl = addBtn.closest('.custom_upsell_card').querySelector('.custom_upsell_error');
        if (errorEl) { errorEl.hidden = true; errorEl.textContent = ''; }

        try {
          await addUpsellItemToCart(variantId, addBtn);
        } catch (err) {
          console.error('Upsell add error:', err);
          if (errorEl) { errorEl.textContent = err.message || 'Unable to add this item. Please try again.'; errorEl.hidden = false; }
        } finally {
          addBtn.disabled = false;
        }

        return;
      }
    }, true);
  }

  /* -------------------------------------------
     THEME-LIKE ADD TO CART FOR DRAWER
  ------------------------------------------- */
  async function addUpsellItemToCart(variantId, triggerEl) {
    const cartDrawer = document.querySelector('cart-drawer');
    const body = buildAddToCartBody(variantId);

    const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      },
      body
    });

    const responseData = await response.json();

    if (!response.ok || responseData.status >= 400) {
      throw new Error(responseData.description || responseData.message || 'Unable to add product');
    }

    if (cartDrawer) {
      if (typeof cartDrawer.setActiveElement === 'function' && triggerEl) {
        cartDrawer.setActiveElement(triggerEl);
      }

      if (typeof cartDrawer.renderContents === 'function' && responseData.sections) {
        cartDrawer.renderContents(responseData);
      } else {
        await refreshCartUI();
      }

      if (typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      } else {
        cartDrawer.classList.add('active');
      }
    } else {
      await refreshCartUI();
    }

    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
    window.dispatchEvent(new CustomEvent('cart:refresh'));
    window.dispatchEvent(new CustomEvent('cart:updated'));

    setTimeout(() => {
      initExistingWrappers();
    }, 120);
  }

  function buildAddToCartBody(variantId) {
    const cartDrawer = document.querySelector('cart-drawer');
    const formData = new FormData();

    formData.append('id', Number(variantId));
    formData.append('quantity', 1);

    if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
      const sections = cartDrawer.getSectionsToRender().map(section => section.id);
      formData.append('sections', sections.join(','));
      formData.append('sections_url', window.location.pathname);
    } else {
      const fallbackSections = getFallbackCartSectionIds();
      if (fallbackSections.length) {
        formData.append('sections', fallbackSections.join(','));
        formData.append('sections_url', window.location.pathname);
      }
    }

    return formData;
  }

  function getFallbackCartSectionIds() {
    return [...new Set(
      [...document.querySelectorAll('[id^="shopify-section-"]')]
        .map(el => el.id.replace('shopify-section-', ''))
        .filter(id => /cart|drawer|bubble|icon/i.test(id))
    )];
  }

  /* -------------------------------------------
     REFRESH CART UI AFTER UPSELL ADD
  ------------------------------------------- */
  async function refreshCartUI() {
    try {
      await refreshCartSections();
    } catch (e) {
      console.warn('Cart sections refresh fallback:', e);
    }

    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
    window.dispatchEvent(new CustomEvent('cart:refresh'));
    window.dispatchEvent(new CustomEvent('cart:updated'));

    setTimeout(() => {
      initExistingWrappers();
    }, 120);
  }

  async function refreshCartSections() {
    const sectionEls = [...document.querySelectorAll('[id^="shopify-section-"]')];
    const sectionIds = [...new Set(
      sectionEls
        .map(el => el.id.replace('shopify-section-', ''))
        .filter(id => /cart|drawer|bubble|icon/i.test(id))
    )];

    if (!sectionIds.length) return;

    const url = `${window.location.pathname}?sections=${encodeURIComponent(sectionIds.join(','))}`;
    const res = await fetch(url, { credentials: 'same-origin' });
    const htmlBySection = await res.json();

    sectionIds.forEach(sectionId => {
      const mount = document.getElementById(`shopify-section-${sectionId}`);
      const newHtml = htmlBySection[sectionId];

      if (mount && newHtml) {
        mount.innerHTML = newHtml;
      }
    });
  }

  /* -------------------------------------------
     VARIANT MATCH + PRICE + IMAGE UPDATE
  ------------------------------------------- */
  function updateVariantForCard(card) {
    const handle = card.dataset.handle;
    const p = productMap.get(handle);
    if (!p) return;

    const variants = p.variants || [];
    const selected = safeParse(card.dataset.selected, {});
    const addBtn = card.querySelector('.custom_upsell_add');
    const priceEl = card.querySelector('.custom_upsell_price');
    const selectedEl = card.querySelector('.custom_upsell_selected');

    if (variants.length === 1) {
      addBtn.disabled = !variants[0].available;
      addBtn.dataset.variantId = variants[0].id;
      if (priceEl) priceEl.textContent = formatMoney(variants[0].price);
      setCardImage(card, p, variants[0]);
      if (selectedEl) selectedEl.textContent = variants[0].available ? 'Ready to add' : 'Sold out';
      return;
    }

    const needed = [1,2,3].filter(i => variants.some(v => v[`option${i}`])).map(i => `option${i}`);
    const missing = needed.filter(k => !selected[k]);

    if (missing.length) {
      addBtn.disabled = true;
      addBtn.dataset.variantId = '';
      if (selectedEl) selectedEl.textContent = 'Select options';
      return;
    }

    const match = variants.find(v => needed.every(k =>
      String(v[k]).toLowerCase() === String(selected[k]).toLowerCase()
    ));

    if (!match) {
      addBtn.disabled = true;
      addBtn.dataset.variantId = '';
      if (selectedEl) selectedEl.textContent = 'Unavailable';
      return;
    }

    addBtn.dataset.variantId = match.id;
    if (priceEl) priceEl.textContent = formatMoney(match.price);
    setCardImage(card, p, match);

    if (!match.available) {
      addBtn.disabled = true;
      if (selectedEl) selectedEl.textContent = 'Sold out';
      return;
    }

    addBtn.disabled = false;
    if (selectedEl) selectedEl.textContent = needed.map(k => selected[k]).join(' / ');
  }

  function setCardImage(card, product, variant) {
    const imgTag = card.querySelector('.custom_upsell_img_tag');
    if (!imgTag) return;

    const src =
      variant?.featured_image?.src ||
      variant?.featured_image ||
      variant?.image ||
      product?.featured_image ||
      product?.images?.[0] ||
      '';

    if (src) imgTag.src = src;
  }

  /* -------------------------------------------
     HELPERS
  ------------------------------------------- */
  function normalizeOptionNames(p) {
    const o = p.options || [];
    if (Array.isArray(o) && o.length && typeof o[0] === 'string') return o;
    if (Array.isArray(o) && o.length && typeof o[0] === 'object') return o.map(x => x.name).filter(Boolean);
    return [];
  }

  function upsellDisplayTitle(product, archiveIds) {
    const title = String(product.title || '');
    return archiveIds.has(String(product.id)) && !/\barchive\b/i.test(title)
      ? `${title} (Archive, final sale)` : title;
  }

  function safeParse(json, fallback) {
    try { return JSON.parse(json); }
    catch (e) { return fallback; }
  }

  function formatMoney(cents) {
    const n = Number(cents);
    const currency = (window.Shopify?.currency?.active) ? Shopify.currency.active : 'AUD';
    try {
      return (n / 100).toLocaleString(undefined, { style: 'currency', currency });
    } catch (e) {
      return '$' + (n / 100).toFixed(2);
    }
  }

  function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, m => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;'
    }[m]));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();















// (() => {
//   const WRAPPER_SELECTOR = '.custom_upsells_wrapper';
//   const productMap = new Map();
//   let observerStarted = false;
//   let globalEventsBound = false;

//   /* -------------------------------------------
//      BOOT / INIT
//   ------------------------------------------- */
//   function boot() {
//     startObserver();
//     bindGlobalEvents();
//     initExistingWrappers();
//   }

//   function startObserver() {
//     if (observerStarted) return;
//     observerStarted = true;

//     const obs = new MutationObserver(() => {
//       initExistingWrappers();
//     });

//     obs.observe(document.documentElement, { childList: true, subtree: true });
//   }

//   function bindGlobalEvents() {
//     if (globalEventsBound) return;
//     globalEventsBound = true;

//     document.addEventListener('cart:refresh', debounceInitExistingWrappers, true);
//     document.addEventListener('cart:updated', debounceInitExistingWrappers, true);
//     document.addEventListener('shopify:section:load', debounceInitExistingWrappers, true);
//     document.addEventListener('shopify:section:reorder', debounceInitExistingWrappers, true);
//     document.addEventListener('shopify:section:select', debounceInitExistingWrappers, true);
//     document.addEventListener('shopify:section:deselect', debounceInitExistingWrappers, true);
//     window.addEventListener('pageshow', debounceInitExistingWrappers);
//   }

//   function debounceInitExistingWrappers() {
//     clearTimeout(window.__customUpsellsInitTimer);
//     window.__customUpsellsInitTimer = setTimeout(() => {
//       initExistingWrappers();
//     }, 80);
//   }

//   function initExistingWrappers() {
//     document.querySelectorAll(WRAPPER_SELECTOR).forEach(wrapper => {
//       if (!wrapper.dataset.upsellsInit) initUpsells(wrapper);
//     });
//   }

//   function initUpsells(wrapper) {
//     wrapper.dataset.upsellsInit = "1";
//     const url = wrapper.getAttribute('data-reco-url');
//     if (!url) return;

//     wrapper.innerHTML = `<div class="custom_upsells_loading">Loading...</div>`;

//     fetch(url, { credentials: 'same-origin' })
//       .then(r => r.json())
//       .then(data => {
//         const products = data?.products || [];
//         if (!products.length) { wrapper.innerHTML = ''; return; }

//         wrapper.innerHTML = `
//           <div class="custom_upsells_section">
//             <h4 class="cart-upsells__heading t-mt-0 t-mb-4 h4 t-text-center">You may also like</h4>

//             <div class="swiper custom-upsells-swiper">
//               <div class="swiper-wrapper"></div>
//             </div>
//           </div>
//         `;

//         const swiperEl = wrapper.querySelector('.custom-upsells-swiper');
//         const track = swiperEl.querySelector('.swiper-wrapper');

//         products.forEach(p => {
//           if ((p.product_type || '').toLowerCase() === 'bundle') return;
//           if (!p.handle || !p.variants?.length) return;

//           productMap.set(p.handle, p);

//           const baseImg = p.featured_image || p.images?.[0] || '';
//           const title = escapeHtml(p.title || '');
//           const productUrl = `/products/${p.handle}`;

//           track.insertAdjacentHTML('beforeend', `
//             <div class="swiper-slide">
//               <div class="custom_upsell_card" data-handle="${p.handle}" data-selected="{}">
//                 <div class="custom_upsell_top">
//                   <a class="custom_upsell_img" href="${productUrl}">
//                     ${baseImg ? `<img class="custom_upsell_img_tag" src="${baseImg}" alt="${title}" loading="lazy">` : ``}
//                   </a>

//                   <div>
//                     <div class="custom_upsell_headline">
//                       <small class="t-font-medium">${title}</small>
//                       <div class="custom_upsell_price"></div>
//                     </div>

//                     <div class="custom_upsell_selected">Select options</div>

//                     <div class="custom_upsell_headline" style="margin-top:-6px;">
//                       <span></span>
//                       <span class="custom_upsell_edit" role="button" tabindex="0">Edit</span>
//                     </div>
//                   </div>
//                 </div>

//                 <div class="custom_upsell_options"></div>

//                 <button class="custom_upsell_add" type="button" disabled data-variant-id="">
//                   ADD
//                 </button>
//               </div>
//             </div>
//           `);
//         });

//         if (!track.children.length) { wrapper.innerHTML = ''; return; }

//         wrapper.querySelectorAll('.custom_upsell_card').forEach(card => {
//           buildOptionPickers(card);
//           autoSelectFirstAvailable(card);
//         });

//         initSwiper(swiperEl);
//         attachInteractions(wrapper);
//       })
//       .catch(err => {
//         console.error('Upsells error:', err);
//         wrapper.innerHTML = '';
//       });
//   }

//   /* -------------------------------------------
//      SWIPER: 1.5 slides, no center
//   ------------------------------------------- */
//   function initSwiper(swiperEl) {
//     if (!swiperEl || !window.Swiper) return;

//     if (swiperEl.swiper) {
//       try { swiperEl.swiper.destroy(true, true); } catch(e){}
//     }

//     new Swiper(swiperEl, {
//       slidesPerView: 1.5,
//       slidesPerGroup: 1,
//       centeredSlides: false,
//       spaceBetween: 12,
//       freeMode: false,
//       speed: 350,
//       preventClicks: false,
//       preventClicksPropagation: false,
//       touchStartPreventDefault: false,
//       observer: true,
//       observeParents: true,
//       watchOverflow: true
//     });
//   }

//   /* -------------------------------------------
//      BUILD OPTION PICKERS
//   ------------------------------------------- */
//   function buildOptionPickers(card) {
//     const handle = card.dataset.handle;
//     const p = productMap.get(handle);
//     if (!p) return;

//     const optsEl = card.querySelector('.custom_upsell_options');
//     optsEl.innerHTML = '';

//     const variants = p.variants || [];
//     const optionNames = normalizeOptionNames(p);

//     if (variants.length === 1) {
//       const edit = card.querySelector('.custom_upsell_edit');
//       if (edit) edit.style.display = 'none';
//       return;
//     }

//     [1, 2, 3].forEach(idx => {
//       const key = `option${idx}`;
//       const values = [...new Set(variants.map(v => v[key]).filter(Boolean))];
//       if (!values.length) return;

//       const name = optionNames[idx - 1] || `Option ${idx}`;
//       const isColor = /color|colour/i.test(name);

//       optsEl.insertAdjacentHTML('beforeend', `
//         <div class="upsell_option" data-option-index="${idx}">
//           <div class="upsell_option_label">${escapeHtml(name)}</div>
//           <div class="upsell_option_values">
//             ${values.map(rawVal => {
//               const label = escapeHtml(rawVal);
//               const encoded = encodeURIComponent(rawVal);

//               if (isColor) {
//                 const Value_ = rawVal.toLowerCase().replace(/\s+/g, '');
//                 const swatchImg_ = `https://myntlab.com/cdn/shop/files/${Value_}fabricswatch.jpg?width=60`;

//                 return `
//                   <button type="button"
//                     class="upsell_opt_btn is-swatch-img"
//                     data-value="${encoded}"
//                     title="${label}"
//                     style="background-image:url('${swatchImg_}')">
//                     <span class="visually-hidden">${label}</span>
//                   </button>
//                 `;
//               }

//               return `
//                 <button type="button"
//                   class="upsell_opt_btn"
//                   data-value="${encoded}">${label}</button>
//               `;
//             }).join('')}
//           </div>
//         </div>
//       `);
//     });

//     if (!optsEl.children.length) {
//       const edit = card.querySelector('.custom_upsell_edit');
//       if (edit) edit.style.display = 'none';
//     }
//   }

//   /* -------------------------------------------
//      AUTO SELECT FIRST AVAILABLE VARIANT
//   ------------------------------------------- */
//   function autoSelectFirstAvailable(card) {
//     const handle = card.dataset.handle;
//     const p = productMap.get(handle);
//     if (!p) return;

//     const variants = p.variants || [];
//     const addBtn = card.querySelector('.custom_upsell_add');
//     const priceEl = card.querySelector('.custom_upsell_price');
//     const selectedEl = card.querySelector('.custom_upsell_selected');

//     const firstAvail = variants.find(v => v.available) || variants[0];
//     if (!firstAvail) return;

//     if (variants.length === 1) {
//       addBtn.disabled = !firstAvail.available;
//       addBtn.dataset.variantId = firstAvail.id;
//       if (priceEl) priceEl.textContent = formatMoney(firstAvail.price);
//       setCardImage(card, p, firstAvail);
//       if (selectedEl) selectedEl.textContent = firstAvail.available ? 'Ready to add' : 'Sold out';
//       return;
//     }

//     const selected = {};
//     if (firstAvail.option1) selected.option1 = firstAvail.option1;
//     if (firstAvail.option2) selected.option2 = firstAvail.option2;
//     if (firstAvail.option3) selected.option3 = firstAvail.option3;

//     card.dataset.selected = JSON.stringify(selected);

//     Object.keys(selected).forEach(k => {
//       const idx = Number(k.replace('option', ''));
//       const val = selected[k];

//       const wrap = card.querySelector(`.upsell_option[data-option-index="${idx}"]`);
//       if (!wrap) return;

//       wrap.querySelectorAll('.upsell_opt_btn').forEach(b => b.classList.remove('is-active'));

//       const btn = [...wrap.querySelectorAll('.upsell_opt_btn')]
//         .find(b => decodeURIComponent(b.dataset.value || '') === val);

//       if (btn) btn.classList.add('is-active');
//     });

//     updateVariantForCard(card);
//   }

//   /* -------------------------------------------
//      INTERACTIONS
//   ------------------------------------------- */
//   function attachInteractions(wrapper) {
//     if (wrapper.dataset.upsellsBound === "1") return;
//     wrapper.dataset.upsellsBound = "1";

//     wrapper.addEventListener('click', async (e) => {
//       const edit = e.target.closest('.custom_upsell_edit');
//       if (edit) {
//         const card = edit.closest('.custom_upsell_card');
//         if (!card) return;

//         card.classList.toggle('is-editing');
//         edit.textContent = card.classList.contains('is-editing') ? 'Close' : 'Edit';
//         return;
//       }

//       const optBtn = e.target.closest('.upsell_opt_btn');
//       if (optBtn) {
//         const card = optBtn.closest('.custom_upsell_card');
//         const optWrap = optBtn.closest('.upsell_option');
//         if (!card || !optWrap) return;

//         optWrap.querySelectorAll('.upsell_opt_btn').forEach(b => b.classList.remove('is-active'));
//         optBtn.classList.add('is-active');

//         const idx = Number(optWrap.dataset.optionIndex);
//         const rawVal = decodeURIComponent(optBtn.dataset.value || '');

//         const selected = safeParse(card.dataset.selected, {});
//         selected[`option${idx}`] = rawVal;
//         card.dataset.selected = JSON.stringify(selected);

//         updateVariantForCard(card);
//         return;
//       }

//       const addBtn = e.target.closest('.custom_upsell_add');
//       if (addBtn) {
//         const variantId = addBtn.dataset.variantId;
//         if (!variantId) return;

//         addBtn.disabled = true;

//         try {
//           await fetch('/cart/add.js', {
//             method: 'POST',
//             credentials: 'same-origin',
//             headers: { 'Content-Type': 'application/json' },
//             body: JSON.stringify({ id: Number(variantId), quantity: 1 })
//           });

//           await refreshCartUI();
//         } catch (err) {
//           console.error('Upsell add error:', err);
//         } finally {
//           addBtn.disabled = false;
//         }

//         return;
//       }
//     }, true);
//   }

//   /* -------------------------------------------
//      REFRESH CART UI AFTER UPSELL ADD
//   ------------------------------------------- */
//   async function refreshCartUI() {
//     try {
//       await refreshCartSections();
//     } catch (e) {
//       console.warn('Cart sections refresh fallback:', e);
//     }

//     document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
//     document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
//     window.dispatchEvent(new CustomEvent('cart:refresh'));
//     window.dispatchEvent(new CustomEvent('cart:updated'));

//     setTimeout(() => {
//       initExistingWrappers();
//     }, 120);
//   }

//   async function refreshCartSections() {
//     const sectionEls = [...document.querySelectorAll('[id^="shopify-section-"]')];
//     const sectionIds = [...new Set(
//       sectionEls
//         .map(el => el.id.replace('shopify-section-', ''))
//         .filter(id => /cart|drawer|bubble|icon/i.test(id))
//     )];

//     if (!sectionIds.length) return;

//     const url = `${window.location.pathname}?sections=${encodeURIComponent(sectionIds.join(','))}`;
//     const res = await fetch(url, { credentials: 'same-origin' });
//     const htmlBySection = await res.json();

//     sectionIds.forEach(sectionId => {
//       const mount = document.getElementById(`shopify-section-${sectionId}`);
//       const newHtml = htmlBySection[sectionId];

//       if (mount && newHtml) {
//         mount.innerHTML = newHtml;
//       }
//     });
//   }

//   /* -------------------------------------------
//      VARIANT MATCH + PRICE + IMAGE UPDATE
//   ------------------------------------------- */
//   function updateVariantForCard(card) {
//     const handle = card.dataset.handle;
//     const p = productMap.get(handle);
//     if (!p) return;

//     const variants = p.variants || [];
//     const selected = safeParse(card.dataset.selected, {});
//     const addBtn = card.querySelector('.custom_upsell_add');
//     const priceEl = card.querySelector('.custom_upsell_price');
//     const selectedEl = card.querySelector('.custom_upsell_selected');

//     if (variants.length === 1) {
//       addBtn.disabled = !variants[0].available;
//       addBtn.dataset.variantId = variants[0].id;
//       if (priceEl) priceEl.textContent = formatMoney(variants[0].price);
//       setCardImage(card, p, variants[0]);
//       if (selectedEl) selectedEl.textContent = variants[0].available ? 'Ready to add' : 'Sold out';
//       return;
//     }

//     const needed = [1,2,3].filter(i => variants.some(v => v[`option${i}`])).map(i => `option${i}`);
//     const missing = needed.filter(k => !selected[k]);

//     if (missing.length) {
//       addBtn.disabled = true;
//       addBtn.dataset.variantId = '';
//       if (selectedEl) selectedEl.textContent = 'Select options';
//       return;
//     }

//     const match = variants.find(v => needed.every(k =>
//       String(v[k]).toLowerCase() === String(selected[k]).toLowerCase()
//     ));

//     if (!match) {
//       addBtn.disabled = true;
//       addBtn.dataset.variantId = '';
//       if (selectedEl) selectedEl.textContent = 'Unavailable';
//       return;
//     }

//     addBtn.dataset.variantId = match.id;
//     if (priceEl) priceEl.textContent = formatMoney(match.price);
//     setCardImage(card, p, match);

//     if (!match.available) {
//       addBtn.disabled = true;
//       if (selectedEl) selectedEl.textContent = 'Sold out';
//       return;
//     }

//     addBtn.disabled = false;
//     if (selectedEl) selectedEl.textContent = needed.map(k => selected[k]).join(' / ');
//   }

//   function setCardImage(card, product, variant) {
//     const imgTag = card.querySelector('.custom_upsell_img_tag');
//     if (!imgTag) return;

//     const src =
//       variant?.featured_image?.src ||
//       variant?.featured_image ||
//       variant?.image ||
//       product?.featured_image ||
//       product?.images?.[0] ||
//       '';

//     if (src) imgTag.src = src;
//   }

//   /* -------------------------------------------
//      HELPERS
//   ------------------------------------------- */
//   function normalizeOptionNames(p) {
//     const o = p.options || [];
//     if (Array.isArray(o) && o.length && typeof o[0] === 'string') return o;
//     if (Array.isArray(o) && o.length && typeof o[0] === 'object') return o.map(x => x.name).filter(Boolean);
//     return [];
//   }

//   function safeParse(json, fallback) {
//     try { return JSON.parse(json); }
//     catch (e) { return fallback; }
//   }

//   function formatMoney(cents) {
//     const n = Number(cents);
//     const currency = (window.Shopify?.currency?.active) ? Shopify.currency.active : 'AUD';
//     try {
//       return (n / 100).toLocaleString(undefined, { style: 'currency', currency });
//     } catch (e) {
//       return '$' + (n / 100).toFixed(2);
//     }
//   }

//   function escapeHtml(str) {
//     return String(str).replace(/[&<>"']/g, m => ({
//       '&': '&amp;',
//       '<': '&lt;',
//       '>': '&gt;',
//       '"': '&quot;',
//       "'": '&#39;'
//     }[m]));
//   }

//   if (document.readyState === 'loading') {
//     document.addEventListener('DOMContentLoaded', boot);
//   } else {
//     boot();
//   }
// })();

(function () {
  function isGraphicGalleryAsset(slide) {
    if (!slide) return false;
    if (slide.classList && (slide.classList.contains('gallery-slide--graphic') || slide.classList.contains('gallery-thumb--graphic'))) return true;
    var alt = (slide.getAttribute('data-alt') || '').toLowerCase();
    var img = slide.querySelector('img');
    var src = img ? (img.currentSrc || img.src || '').toLowerCase() : '';
    return (
      alt.indexOf('ingredient') !== -1 ||
      alt.indexOf('myntfusion blend') !== -1 ||
      alt.indexOf('eucalyptus pine mint') !== -1 ||
      src.indexOf('fabric-story') !== -1 ||
      src.indexOf('benefits') !== -1 ||
      src.indexOf('drape-guide') !== -1 ||
      src.indexOf('size-guide') !== -1 ||
      src.indexOf('ingredient-gallery') !== -1
    );
  }

  function ensureGalleryPillStyle() {
    if (document.getElementById('mynt-gallery-pill-sync-style')) return;
    var style = document.createElement('style');
    style.id = 'mynt-gallery-pill-sync-style';
    style.textContent = '.gallery.gallery-graphic-active .gallery-range-pill{display:flex!important;align-items:center!important;max-width:calc(100% - 20px)!important;}.gallery.gallery-graphic-active .gallery-slide img,.gallery .gallery-slide--graphic img,.gallery .gallery-slide img[src*="fabric-story"],.gallery .gallery-slide img[src*="benefits"],.gallery .gallery-slide img[src*="drape-guide"],.gallery .gallery-slide img[src*="size-guide"],.gallery .gallery-slide img[src*="ingredient-gallery"]{object-fit:contain!important;object-position:center center!important;background:#fff!important;transform:none!important;}';
    document.head.appendChild(style);
  }

  function getSelectedGalleryAsset(gallery) {
    var strip = gallery.querySelector('.gallery-strip');
    if (!strip) return null;
    var activeThumb = gallery.querySelector('.gallery-thumb.active, .gallery-thumb.is-active, .gallery-thumb[aria-current="true"]');
    if (activeThumb) {
      var activeIndex = activeThumb.getAttribute('data-index');
      var activeSlide = activeIndex !== null ? strip.querySelector('.gallery-slide[data-index="' + activeIndex + '"]') : null;
      return activeSlide || activeThumb;
    }
    var slides = strip.querySelectorAll('.gallery-slide');
    var closest = null;
    var minDistance = Infinity;
    for (var i = 0; i < slides.length; i += 1) {
      var distance = Math.abs(slides[i].offsetLeft - strip.scrollLeft);
      if (distance < minDistance) {
        minDistance = distance;
        closest = slides[i];
      }
    }
    return closest;
  }

  function syncGalleryPill(gallery) {
    if (!gallery) return;
    ensureGalleryPillStyle();
    var activeAsset = getSelectedGalleryAsset(gallery);
    gallery.classList.toggle('gallery-graphic-active', isGraphicGalleryAsset(activeAsset));
  }

  function normaliseTrustRowText() {
    document.querySelectorAll('.trust-item p').forEach(function (item) {
      if (item.textContent.trim().toLowerCase() === 'free au shipping') {
        item.textContent = 'Free shipping';
      }
    });
  }

  function ensurePdpRepairStyle() {
    if (document.getElementById('mynt-pdp-mobile-repair-style')) return;
    var style = document.createElement('style');
    style.id = 'mynt-pdp-mobile-repair-style';
    style.textContent = [
      '.section-announcement .mynt-announcement,.section-announcement .mynt-announcement *{background:#173d35!important;background-color:#173d35!important;}',
      '.section-announcement .mynt-announcement__text,.section-announcement .mynt-announcement__text *,.section-announcement .mynt-announcement__text a{color:#eafff3!important;}',
      '.buy-box-reviews .review-snippet{font-size:13px;font-style:italic;color:#173d35;opacity:.7;margin:0 0 6px;line-height:1.4;}',
      '.buy-box-reviews .review-snippet strong{font-weight:700;font-style:italic;}',
      '.buy-box-price{display:flex!important;flex-wrap:wrap!important;align-items:baseline!important;gap:6px 12px!important;min-height:54px!important;}',
      '.buy-box-price .price-current,.buy-box-price .price-compare{display:inline-block!important;}',
      '.buy-box-price .price-save{display:inline-flex!important;align-items:center!important;line-height:1!important;margin-left:0!important;}',
      '.buy-box-price .per-night{flex-basis:100%!important;margin-left:0!important;white-space:normal!important;line-height:1.35!important;}',
      'html.mynt-balance-fitted-pdp .colour-swatches{gap:14px!important;padding-left:8px!important;}',
      'html.mynt-balance-fitted-pdp .gallery .gallery-slide--story-graphic img,html.mynt-balance-fitted-pdp .gallery .gallery-slide img[src*="fabric-story"]{object-fit:contain!important;object-position:center center!important;transform:none!important;background:#fff!important;}',
      'html.mynt-balance-fitted-pdp .gallery.gallery-graphic-active .gallery-range-pill,html.mynt-balance-fitted-pdp .gallery .gallery-range-pill{display:flex!important;align-items:center!important;}',
      'html.mynt-balance-fitted-pdp .mynt-hidden-broken-card{display:none!important;}',
      '.gallery-slide--myntbalance-ingredients{background:#fff!important;}',
      '.gallery-slide--myntbalance-ingredients img,.gallery-thumb--myntbalance-ingredients img{width:100%!important;height:100%!important;object-fit:contain!important;object-position:center center!important;display:block!important;background:#fff!important;}',
      '.mb-ingredient-card{width:100%;height:100%;box-sizing:border-box;padding:18px;background:linear-gradient(135deg,#fffaf5 0%,#f7eee7 46%,#f8fbf7 100%);display:grid;grid-template-columns:1fr;gap:12px;color:#173d35;}',
      '.mb-ingredient-copy{display:flex;flex-direction:column;justify-content:center;min-width:0;}',
      '.mb-ingredient-eyebrow{font-family:Poppins,sans-serif;font-size:9px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;color:#c77d60;margin-bottom:8px;}',
      '.mb-ingredient-copy h3{font-family:Recoleta,Georgia,serif;font-size:27px;line-height:1.04;font-weight:400;margin:0 0 10px;color:#173d35;}',
      '.mb-ingredient-copy p{font-size:11px;line-height:1.55;margin:0 0 12px;color:#173d35;opacity:.68;}',
      '.mb-ingredient-points{display:grid;gap:7px;}',
      '.mb-ingredient-points div{display:grid;grid-template-columns:10px 1fr;column-gap:8px;align-items:start;font-size:10px;line-height:1.3;}',
      '.mb-ingredient-points span{width:6px;height:6px;border-radius:50%;background:#c77d60;margin-top:5px;}',
      '.mb-ingredient-points strong{font-family:Poppins,sans-serif;font-weight:700;font-size:10px;text-transform:uppercase;letter-spacing:.2px;}',
      '.mb-ingredient-points small{grid-column:2;display:block;font-size:10px;opacity:.68;}',
      '.mb-ingredient-media{display:grid;grid-template-columns:1.15fr .85fr;gap:8px;min-height:0;}',
      '.mb-ingredient-tile{position:relative;overflow:hidden;border-radius:12px;min-height:74px;background:#fff;}',
      '.mb-ingredient-tile-large{grid-row:span 2;}',
      '.mb-ingredient-tile img{width:100%;height:100%;object-fit:cover;}',
      '.mb-ingredient-tile span,.mb-ingredient-thumb span:first-child,.mb-ingredient-thumb span:last-child{font-family:Poppins,sans-serif;font-weight:700;letter-spacing:.7px;text-transform:uppercase;}',
      '.mb-ingredient-tile span{position:absolute;left:8px;bottom:8px;max-width:calc(100% - 16px);padding:4px 7px;border-radius:999px;background:rgba(255,255,255,.9);color:#173d35;font-size:8px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}',
      '.mb-ingredient-thumb{width:100%;height:100%;display:flex;flex-direction:column;align-items:center;justify-content:center;background:#fff6ef;color:#c77d60;font-size:9px;line-height:1.25;text-align:center;}',
      '@media(max-width:768px){html.mynt-balance-fitted-pdp .gallery-main{height:min(112vw,430px)!important;}html.mynt-balance-fitted-pdp .gallery-range-pill{bottom:8px!important;left:10px!important;font-size:9px!important;padding:4px 10px!important;letter-spacing:.8px!important;}}'
    ].join('');
    document.head.appendChild(style);
  }

  function repairAnnouncementBar() {
    ensurePdpRepairStyle();
    document.querySelectorAll('.section-announcement .mynt-announcement').forEach(function (bar) {
      bar.style.background = '#173d35';
      bar.style.backgroundColor = '#173d35';
    });
    document.querySelectorAll('.section-announcement .mynt-announcement__text, .section-announcement .mynt-announcement__text *').forEach(function (el) {
      el.style.color = '#eafff3';
    });
  }

  function isMyntFusionFlatSheetPdp() {
    return /\/products\/myntfusion-cooling-flat-sheet\/?$/i.test(window.location.pathname);
  }

  function isMyntBalanceFittedSheetPdp() {
    return /\/products\/temperature-balancing-fitted-sheet\/?$/i.test(window.location.pathname);
  }

  function isMyntBalancePdp() {
    return /\/products\/temperature-balancing-(flat-sheet|fitted-sheet|pillowcase|duvet-cover)\/?$/i.test(window.location.pathname);
  }

  var myntProductJson = null;
  var myntProductJsonLoading = false;

  function formatBalanceMoney(cents) {
    return '$' + (Number(cents || 0) / 100).toFixed(2);
  }

  function loadProductJsonForPrice() {
    if (myntProductJson || myntProductJsonLoading || !window.fetch) return;
    myntProductJsonLoading = true;
    window.fetch(window.location.pathname.replace(/\/$/, '') + '.js', { credentials: 'same-origin' })
      .then(function (response) { return response.ok ? response.json() : null; })
      .then(function (data) {
        myntProductJson = data;
        repairBalancePerNight();
      })
      .catch(function () {})
      .finally(function () { myntProductJsonLoading = false; });
  }

  function getVariantIdForPrice() {
    var input = document.querySelector('input[name="id"], #variantId');
    return input ? String(input.value || input.getAttribute('value') || '') : '';
  }

  function getActiveOptionForPrice(selector, attr) {
    var active = document.querySelector(selector + '.active, ' + selector + '.selected, ' + selector + '[aria-pressed="true"]');
    return active ? String(active.getAttribute(attr) || active.textContent || '').trim() : '';
  }

  function getProductVariantsForPrice() {
    if (myntProductJson && Array.isArray(myntProductJson.variants)) return myntProductJson.variants;
    var metaProduct = window.meta && window.meta.product;
    return metaProduct && Array.isArray(metaProduct.variants) ? metaProduct.variants : [];
  }

  function getBalanceSelectedVariant() {
    var variants = getProductVariantsForPrice();
    if (!variants.length) {
      loadProductJsonForPrice();
      return null;
    }
    var selectedId = getVariantIdForPrice();
    var byId = selectedId ? variants.find(function (variant) { return String(variant.id) === selectedId; }) : null;
    if (byId) return byId;

    var colour = getActiveOptionForPrice('.colour-swatch', 'data-colour').toLowerCase();
    var size = getActiveOptionForPrice('.size-btn', 'data-size').toLowerCase();
    var pack = getActiveOptionForPrice('.pack-option, .pack-card, .pack-btn', 'data-pack').toLowerCase();
    return variants.find(function (variant) {
      var title = String(variant.public_title || variant.title || variant.name || '').toLowerCase();
      return (!colour || title.indexOf(colour) !== -1) &&
        (!size || title.indexOf(size) !== -1) &&
        (!pack || title.indexOf(pack) !== -1);
    }) || variants[0];
  }

  function repairBalancePerNight() {
    if (!isMyntBalancePdp() && !isMyntFusionFlatSheetPdp()) return;
    ensurePdpRepairStyle();
    loadProductJsonForPrice();
    var variant = getBalanceSelectedVariant();
    var priceEl = document.querySelector('.buy-box-price');
    if (!variant || !priceEl || !variant.price) return;
    var price = Number(variant.price || 0);
    var compare = Number(variant.compare_at_price || variant.compare_at || 0);
    var nightly = formatBalanceMoney(Math.round(price / 365));
    var current = priceEl.querySelector('.price-current');
    var compareEl = priceEl.querySelector('.price-compare');
    var saveEl = priceEl.querySelector('.price-save');
    var nightlyAmount = priceEl.querySelector('[data-per-night-amount]');
    var perNight = priceEl.querySelector('.per-night');

    if (!current || !perNight) {
      var priceHtml = '';
      if (compare > price) {
        var savePercent = Math.round(((compare - price) / compare) * 100);
        priceHtml = '<span class="price-compare" style="text-decoration:line-through;opacity:.45;">' + formatBalanceMoney(compare) + '</span>' +
          '<span class="price-current">' + formatBalanceMoney(price) + '</span>' +
          '<span class="price-save">Save ' + savePercent + '%</span>';
      } else {
        priceHtml = '<span class="price-current">' + formatBalanceMoney(price) + '</span>';
      }
      priceEl.innerHTML = priceHtml + '<span class="per-night">About <span data-per-night-amount>' + nightly + '</span>/night over a year</span>';
      return;
    }

    current.textContent = formatBalanceMoney(price);
    if (compareEl) compareEl.textContent = compare > price ? formatBalanceMoney(compare) : '';
    if (saveEl && compare > price) saveEl.textContent = 'Save ' + Math.round(((compare - price) / compare) * 100) + '%';
    if (nightlyAmount) nightlyAmount.textContent = nightly;
    else perNight.textContent = 'About ' + nightly + '/night over a year';
  }

  function scheduleBalancePriceRepair() {
    window.requestAnimationFrame(repairBalancePerNight);
    window.requestAnimationFrame(syncBalanceIngredientImages);
    [80, 260, 760, 1620].forEach(function (delay) {
      window.setTimeout(repairBalancePerNight, delay);
      window.setTimeout(syncBalanceIngredientImages, delay);
    });
  }

  function renumberGalleryFrom(gallery, fromIndex) {
    gallery.querySelectorAll('.gallery-slide, .gallery-thumb').forEach(function (item) {
      var index = Number(item.getAttribute('data-index'));
      if (!Number.isNaN(index) && index >= fromIndex) item.setAttribute('data-index', String(index + 1));
    });
  }

  function scrollGalleryToIndex(gallery, index) {
    var strip = gallery.querySelector('.gallery-strip');
    var slide = strip && strip.querySelector('.gallery-slide[data-index="' + index + '"]');
    if (!strip || !slide) return;
    strip.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
    gallery.querySelectorAll('.gallery-thumb').forEach(function (thumb) {
      thumb.classList.toggle('active', thumb.getAttribute('data-index') === String(index));
    });
    syncGalleryPill(gallery);
  }

  function getBalanceIngredientImageSrc() {
    var colour = getActiveOptionForPrice('.colour-swatch', 'data-colour').toLowerCase();
    if (colour.indexOf('latte') !== -1) {
      return 'https://cdn.shopify.com/s/files/1/0331/4751/9116/t/103/assets/myntbalance-flat-sheet-latte-ingredient-gallery.png?v=1781843824';
    }
    if (colour.indexOf('petal') !== -1) {
      return 'https://cdn.shopify.com/s/files/1/0331/4751/9116/t/103/assets/myntbalance-flat-sheet-petal-ingredient-gallery.png?v=1781843826';
    }
    return 'https://cdn.shopify.com/s/files/1/0331/4751/9116/t/103/assets/myntbalance-flat-sheet-snow-ingredient-gallery.png?v=1781843821';
  }

  function syncBalanceIngredientImages() {
    if (!isMyntBalancePdp()) return;
    var src = getBalanceIngredientImageSrc();
    document.querySelectorAll('.myntbalance-ingredient-gallery-image').forEach(function (image) {
      if (image.getAttribute('src') !== src) image.setAttribute('src', src);
    });
  }

  function ensureBalanceIngredientGraphic() {
    if (!isMyntBalancePdp()) return;
    ensurePdpRepairStyle();
    var imageSrc = getBalanceIngredientImageSrc();
    document.querySelectorAll('.gallery').forEach(function (gallery) {
      var strip = gallery.querySelector('.gallery-strip');
      var thumbs = gallery.querySelector('.gallery-thumbs');
      if (!strip || !thumbs) return;
      if (strip.querySelector('.gallery-slide--myntbalance-ingredients')) {
        syncBalanceIngredientImages();
        return;
      }
      var insertIndex = 1;
      renumberGalleryFrom(gallery, insertIndex);

      var slide = document.createElement('div');
      slide.className = 'gallery-slide gallery-slide--graphic gallery-slide--myntbalance-ingredients';
      slide.setAttribute('data-index', String(insertIndex));
      slide.setAttribute('data-alt', 'MyntBalance ingredients graphic');
      slide.innerHTML = '<img class="myntbalance-ingredient-gallery-image" src="' + imageSrc + '" alt="MyntBalance ingredients graphic" loading="lazy">';
      var beforeSlide = strip.querySelector('.gallery-slide[data-index="2"]');
      strip.insertBefore(slide, beforeSlide || null);

      var thumb = document.createElement('div');
      thumb.className = 'gallery-thumb gallery-thumb--graphic gallery-thumb--myntbalance-ingredients';
      thumb.setAttribute('data-index', String(insertIndex));
      thumb.setAttribute('data-alt', 'MyntBalance ingredients graphic');
      thumb.innerHTML = '<img class="myntbalance-ingredient-gallery-image" src="' + imageSrc + '" alt="MyntBalance ingredients graphic" loading="lazy">';
      thumb.addEventListener('click', function () { scrollGalleryToIndex(gallery, insertIndex); });
      var beforeThumb = thumbs.querySelector('.gallery-thumb[data-index="2"]');
      thumbs.insertBefore(thumb, beforeThumb || null);
    });
  }

  function hideBrokenBalanceRecommendationCards() {
    ['.cross-sell', '.also-like'].forEach(function (selector) {
      var section = document.querySelector(selector);
      if (!section) return;
      var cards = section.querySelectorAll('.cross-sell-card, .also-like-card');
      cards.forEach(function (card) {
        var img = card.querySelector('img');
        var src = img ? String(img.getAttribute('src') || img.currentSrc || img.src || '') : '';
        var hasBrokenImage = src.indexOf('Liquid error') !== -1 || src.indexOf('invalid url input') !== -1;
        var hasUsefulText = !!String(card.textContent || '').replace(/sold out|from/ig, '').trim();
        if (hasBrokenImage || !hasUsefulText) card.classList.add('mynt-hidden-broken-card');
      });
      var visibleCards = Array.from(cards).filter(function (card) {
        return !card.classList.contains('mynt-hidden-broken-card');
      });
      if (!visibleCards.length) section.classList.add('mynt-hidden-broken-card');
    });
  }

  function repairBalanceFittedPdp() {
    if (!isMyntBalanceFittedSheetPdp()) return;
    document.documentElement.classList.add('mynt-balance-fitted-pdp');
    repairBalancePerNight();
    ensureBalanceIngredientGraphic();
    hideBrokenBalanceRecommendationCards();
  }

  function repairBalancePdpFallbacks() {
    if (!isMyntBalancePdp()) return;
    if (isMyntBalanceFittedSheetPdp()) document.documentElement.classList.add('mynt-balance-fitted-pdp');
    repairBalancePerNight();
    ensureBalanceIngredientGraphic();
    hideBrokenBalanceRecommendationCards();
  }

  function repairFusionReviewQuote() {
    var reviews = document.querySelector('.buy-box-reviews');
    if (!reviews || reviews.querySelector('.review-snippet')) return;
    var snippet = document.createElement('p');
    snippet.className = 'review-snippet';
    snippet.innerHTML = '"<strong>So soft and silky. Fantastic to sleep in!</strong>" - Belinda H.';
    reviews.insertBefore(snippet, reviews.firstChild);
  }

  function repairFusionMadeForYouCopy() {
    document.querySelectorAll('h3, h4, p, span, div').forEach(function (el) {
      var text = el.textContent ? el.textContent.trim() : '';
      if (!/^fresh sheet people$/i.test(text)) return;
      el.textContent = 'Sensitive skin';
      var card = el.closest('.perfect-card, .benefit-card, .info-card, .made-for-you-card, .feature-card, .card') || el.parentElement;
      if (!card) return;
      var copy = card.querySelector('p');
      if (copy && copy !== el) {
        copy.textContent = 'OEKO-TEX certified, hypoallergenic, and 99.7% antibacterial. Smooth against skin and made to feel fresh between washes.';
      }
    });
  }

  function renderFusionComparison(section, selected, force) {
    var table = section.querySelector('.comp-table');
    if (!table) return;
    var rows = [
      'Stays cool',
      'Stays fresh between washes',
      'Machine washable',
      'Hypoallergenic',
      'Closed-loop sustainable process',
      'Sustainably made'
    ];
    var data = {
      cotton: [0, 0, 1, 0, 0, 0],
      bamboo: [1, 0, 1, 1, 0, 1],
      silk: [0, 0, 0, 1, 0, 0]
    };
    var names = { cotton: 'Conventional cotton', bamboo: 'Bamboo viscose', silk: 'Mulberry silk' };
    var comp = data[selected] ? selected : 'cotton';
    if (!force && table.dataset.myntFusionRenderedComp === comp) return;
    table.dataset.myntFusionRenderedComp = comp;
    table.innerHTML =
      '<div class="comp-th comp-th-empty"></div>' +
      '<div class="comp-th comp-th-comp">' + names[comp] + '</div>' +
      '<div class="comp-th comp-th-mynt">MyntFusion&#8482;</div>' +
      rows.map(function (row, index) {
        var competitorHas = data[comp][index];
        return '<div class="comp-td comp-td-label">' + row + '</div>' +
          '<div class="comp-td">' + (competitorHas ? '<span class="check">&#10003;</span>' : '<span class="cross">&#10007;</span>') + '</div>' +
          '<div class="comp-td comp-td-mynt"><span class="check">&#10003;</span></div>';
      }).join('');
  }

  function repairFusionComparisonTabs() {
    if (!isMyntFusionFlatSheetPdp()) return;
    document.querySelectorAll('.comparison-section').forEach(function (section) {
      var tabs = section.querySelector('.comp-tabs');
      var table = section.querySelector('.comp-table');
      if (!tabs || !table) return;
      if (!tabs.querySelector('[data-comp="bamboo"]')) {
        var silk = tabs.querySelector('[data-comp="silk"]');
        var bamboo = document.createElement('button');
        bamboo.className = 'comp-tab';
        bamboo.type = 'button';
        bamboo.dataset.comp = 'bamboo';
        bamboo.textContent = 'vs Bamboo viscose';
        tabs.insertBefore(bamboo, silk || null);
      }
      if (tabs.dataset.myntFusionRepairBound !== '1') {
        tabs.dataset.myntFusionRepairBound = '1';
        tabs.addEventListener('click', function (event) {
          var btn = event.target.closest('.comp-tab');
          if (!btn) return;
          tabs.querySelectorAll('.comp-tab').forEach(function (tab) { tab.classList.remove('active'); });
          btn.classList.add('active');
          renderFusionComparison(section, btn.dataset.comp, true);
        });
      }
      var active = tabs.querySelector('.comp-tab.active') || tabs.querySelector('[data-comp="cotton"]') || tabs.querySelector('.comp-tab');
      renderFusionComparison(section, active ? active.dataset.comp : 'cotton', false);
    });
  }

  function repairFusionFlatSheetPdp() {
    if (!isMyntFusionFlatSheetPdp()) return;
    repairFusionReviewQuote();
    repairFusionMadeForYouCopy();
    repairFusionComparisonTabs();
  }

  function repairPdpMobileFallbacks() {
    repairAnnouncementBar();
    repairFusionFlatSheetPdp();
    repairBalancePdpFallbacks();
  }

  function syncAllGalleryPills() {
    repairPdpMobileFallbacks();
    normaliseTrustRowText();
    if (isMyntFusionFlatSheetPdp()) return;
    document.querySelectorAll('.gallery').forEach(syncGalleryPill);
  }

  function scheduleSync() {
    if (isMyntFusionFlatSheetPdp()) return;
    window.requestAnimationFrame(syncAllGalleryPills);
    window.setTimeout(syncAllGalleryPills, 120);
    window.setTimeout(syncAllGalleryPills, 420);
    window.setTimeout(syncAllGalleryPills, 900);
  }

  function bindMobileGallerySwipe() {
    document.querySelectorAll('.gallery').forEach(function (gallery) {
      if (gallery.dataset.myntMobileSwipeBound === '1') return;
      var strip = gallery.querySelector('.gallery-strip');
      if (!strip) return;
      gallery.dataset.myntMobileSwipeBound = '1';

      var startX = 0;
      var startY = 0;
      var startScroll = 0;
      var isDragging = false;
      var didMove = false;

      function slides() {
        return Array.prototype.slice.call(strip.querySelectorAll('.gallery-slide'));
      }

      function currentIndex() {
        var list = slides();
        if (!list.length) return 0;
        var closest = 0;
        var closestDistance = Infinity;
        list.forEach(function (slide, index) {
          var distance = Math.abs(slide.offsetLeft - strip.scrollLeft);
          if (distance < closestDistance) {
            closest = index;
            closestDistance = distance;
          }
        });
        return closest;
      }

      function goTo(index) {
        var list = slides();
        if (!list.length) return;
        var clamped = Math.max(0, Math.min(index, list.length - 1));
        strip.scrollTo({ left: list[clamped].offsetLeft, behavior: 'smooth' });
        gallery.querySelectorAll('.gallery-thumb').forEach(function (thumb) {
          thumb.classList.toggle('active', thumb.getAttribute('data-index') === String(clamped));
        });
      }

      strip.addEventListener('touchstart', function (event) {
        if (!event.touches || event.touches.length !== 1) return;
        startX = event.touches[0].clientX;
        startY = event.touches[0].clientY;
        startScroll = strip.scrollLeft;
        isDragging = true;
        didMove = false;
      }, { passive: true });

      strip.addEventListener('touchmove', function (event) {
        if (!isDragging || !event.touches || event.touches.length !== 1) return;
        var dx = event.touches[0].clientX - startX;
        var dy = event.touches[0].clientY - startY;
        if (Math.abs(dx) <= Math.abs(dy) || Math.abs(dx) < 8) return;
        didMove = true;
        event.preventDefault();
        strip.scrollLeft = startScroll - dx;
      }, { passive: false });

      strip.addEventListener('touchend', function (event) {
        if (!isDragging) return;
        isDragging = false;
        var touch = event.changedTouches && event.changedTouches[0];
        if (!touch || !didMove) return;
        var dx = touch.clientX - startX;
        if (Math.abs(dx) < 36) {
          goTo(currentIndex());
          return;
        }
        goTo(currentIndex() + (dx < 0 ? 1 : -1));
      }, { passive: true });
    });
  }

  function bootGalleryPillSync() {
    bindMobileGallerySwipe();
    syncAllGalleryPills();
    document.querySelectorAll('.gallery-strip').forEach(function (strip) {
      if (strip.dataset.myntGalleryPillSync === '1') return;
      strip.dataset.myntGalleryPillSync = '1';
      var ticking = false;
      strip.addEventListener('scroll', function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          ticking = false;
          syncGalleryPill(strip.closest('.gallery'));
        });
      }, { passive: true });
    });
  }

  document.addEventListener('click', function (event) {
    if (event.target.closest('.gallery-thumb, .gallery-nav')) {
      scheduleSync();
    }
    if (event.target.closest('.colour-swatch, .size-btn, .pack-option, .pack-card, .pack-btn')) {
      scheduleBalancePriceRepair();
    }
  }, true);

  document.addEventListener('change', function (event) {
    if (event.target.closest('input[name="id"], select, input[type="radio"]')) {
      scheduleBalancePriceRepair();
    }
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootGalleryPillSync);
  } else {
    bootGalleryPillSync();
  }
  if (!isMyntFusionFlatSheetPdp()) {
    window.setTimeout(bootGalleryPillSync, 250);
    window.setTimeout(bootGalleryPillSync, 900);
    window.setTimeout(bootGalleryPillSync, 1800);
  }

  window.addEventListener('load', bootGalleryPillSync);
  window.MyntGalleryPillSyncLoaded = true;
  window.MyntPdpMobileRepairLoaded = true;
})();
